import * as React from 'react'
import styled from 'styled-components'

import { UserData } from '@ally/transmitigator'
import { Box, Space, TextBody } from '@ally/metronome-ui'
import { Private } from '@ally/private'

import { formatAddress } from '../../Utils'

const UsernameBox = styled(Box)`
  ${({ theme: { colors, space } }): string => `
    border: 1px solid ${colors['slate-2']};
    padding: ${space.sm} ${space.md};
    margin-top: ${space.xs};
    border-radius: ${space.xxs};
  `}
  box-shadow: 0 2px 4px -2px rgba(0,0,0,0.16), 0 5px 8px -4px rgba(0,0,0,0.16);
  max-width: 500px;
  position: relative;
  z-index: 2;
`

const StyledUserDataContent = styled(Box)`
  ${({ theme: { colors, space } }): string => `
    background-color: ${colors['gray-100']};
    padding: ${space.md};
    border-radius: 0 0 ${space.xxs} ${space.xxs};
  `}
  max-width: 500px;
  position: relative;
  z-index: 1;
`

interface UserDataBoxLineItemProps {
  label: string
  value: string
}

const UserDataBoxLineItem: React.FC<UserDataBoxLineItemProps> = ({
  label,
  value,
}) => {
  if (!value || value.length === 0) {
    return null
  }

  return (
    <TextBody size="sm" tag="p">
      <TextBody size="sm" tag="b" weight="bold">
        {label}:
      </TextBody>{' '}
      <Private as="span">{value}</Private>
    </TextBody>
  )
}

interface UserDataBoxProps {
  userData: UserData | null
  shouldShowAdditionalCopy?: boolean
}

const UserDataBox: React.FC<UserDataBoxProps> = ({
  userData,
  shouldShowAdditionalCopy,
}) => {
  if (!userData) {
    return null
  }

  const {
    email,
    homePhone,
    mobilePhone,
    workPhone,
    physicalAddress,
    alertEmail,
    alertPhone,
  } = userData
  const shouldShowAlertEmail = email !== alertEmail
  const shouldShowAlertPhone = ![homePhone, mobilePhone, workPhone].includes(
    alertPhone,
  )
  // Alerts section should only be shown if both phone and email are NOT matches
  const shouldShowAlertSection = shouldShowAlertEmail && shouldShowAlertPhone

  return (
    <StyledUserDataContent>
      {shouldShowAdditionalCopy && (
        <Space mb="sm">
          <TextBody size="sm" tag="p" data-testid="sub-copy">
            Give this information a once-over — you can edit it later in your{' '}
            <TextBody weight="bold" size="sm" tag="span">
              Profile and Settings
            </TextBody>
            .
          </TextBody>
        </Space>
      )}
      <UserDataBoxLineItem label="Email" value={email} />
      <UserDataBoxLineItem label="Home phone" value={homePhone} />
      <UserDataBoxLineItem label="Mobile phone" value={mobilePhone} />
      <UserDataBoxLineItem label="Work phone" value={workPhone} />
      <UserDataBoxLineItem
        label="Address"
        value={formatAddress(physicalAddress)}
      />
      {shouldShowAlertSection && (
        <Space mt="md">
          <TextBody size="sm" tag="p" weight="bold">
            We&apos;ll send Ally alerts to:
          </TextBody>
          <UserDataBoxLineItem label="Email" value={alertEmail} />
          <UserDataBoxLineItem label="Phone" value={alertPhone} />
        </Space>
      )}
    </StyledUserDataContent>
  )
}

interface AccountDetailsProps {
  username: string
  userData: UserData | null
  shouldShowAdditionalCopy?: boolean
}

const AccountDetails: React.FC<AccountDetailsProps> = ({
  username,
  userData,
  shouldShowAdditionalCopy = false,
}) => {
  return (
    <Box mb="md">
      <UsernameBox>
        <TextBody size="md" tag="b" weight="bold">
          Username:
        </TextBody>
        <Space ml="xs">
          <TextBody size="md" tag="span">
            {username}
          </TextBody>
        </Space>
      </UsernameBox>

      <UserDataBox
        userData={userData}
        shouldShowAdditionalCopy={shouldShowAdditionalCopy}
      />
    </Box>
  )
}

export default AccountDetails
